import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import type { AdminSection } from '../adminSections';
import AdminItemAccordion from './AdminItemAccordion';
import AdminItemPlaceholder from './AdminItemPlaceholder';

interface AdminAccordionSectionProps {
  section: AdminSection;
  visibleItemIds: Set<string>;
  // Sections are multi-open — the parent tracks which are expanded.
  expanded: boolean;
  onToggle: (sectionId: string) => void;
}

const AdminAccordionSection = ({
  section,
  visibleItemIds,
  expanded,
  onToggle,
}: AdminAccordionSectionProps) => {
  // Local state: which item sub-accordion is open. Only one item open at a time
  // per section per D.4 § 6.5 S2 — keeps Access (6 items) visually controlled.
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  const visibleItems = section.items.filter((i) => visibleItemIds.has(i.id));
  if (visibleItems.length === 0) return null;

  const isSingleItem = visibleItems.length === 1;
  const theOnlyItem = isSingleItem ? visibleItems[0] : null;

  const handleItemToggle = (itemId: string) => {
    setExpandedItemId((prev) => (prev === itemId ? null : itemId));
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={() => onToggle(section.id)}
      disableGutters
      slotProps={{ transition: { unmountOnExit: true } }}
      sx={{
        mb: 1,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        boxShadow: 'none',
        overflow: 'hidden',
        '&.Mui-expanded': { margin: '0 0 8px 0' },
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`admin-section-${section.id}-content`}
        id={`admin-section-${section.id}-summary`}
        sx={(theme) => ({
          px: 2,
          minHeight: 56,
          // Section header sits a step darker than the body so the top bar
          // reads as the clickable landmark for the group. Expanded state
          // bumps a hair darker and adds a divider underline.
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.05)'
              : 'rgba(0,0,0,0.04)',
          '&:hover': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.08)'
                : 'rgba(0,0,0,0.07)',
          },
          '&.Mui-expanded': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.08)'
                : 'rgba(0,0,0,0.07)',
            borderBottom: 1,
            borderColor: 'divider',
          },
          '&.Mui-expanded:hover': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.08)'
                : 'rgba(0,0,0,0.07)',
          },
        })}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            {section.label}
          </Typography>
          {visibleItems.length > 1 && (
            <Badge
              badgeContent={visibleItems.length}
              color="primary"
              sx={{ '& .MuiBadge-badge': { position: 'relative', transform: 'none' } }}
            />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2 }}>
        {isSingleItem && theOnlyItem ? (
          // Single-item section: render the item content directly. If it's a live
          // component we embed it; otherwise use the placeholder card.
          theOnlyItem.mode === 'live' && theOnlyItem.component ? (
            theOnlyItem.wrapper ? (
              theOnlyItem.wrapper(<theOnlyItem.component />)
            ) : (
              <theOnlyItem.component />
            )
          ) : (
            <AdminItemPlaceholder item={theOnlyItem} />
          )
        ) : (
          <Box>
            {visibleItems.map((item) => (
              <AdminItemAccordion
                key={item.id}
                item={item}
                expandedItemId={expandedItemId}
                onToggle={handleItemToggle}
              />
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default AdminAccordionSection;
